import landlordAxios from '../../../services/landlordAxios'

const state = {
    dashboard: null,
    aggregate: null,
    shops: [],
    shopsMeta: null,
    currentShop: null,
    societies: [],
    societiesMeta: null,
    currentSociety: null,
    loadingDashboard: false,
    loadingAggregate: false,
    modules: [],
    plans: [],
    countries: [],
    sectors: [],
    tickets: [],
    ticketsMeta: null,
    supportSettings: [],
    supportContactSettings: null,
}

const getters = {
    dashboard: (state) => state.dashboard,
    aggregate: (state) => state.aggregate,
    shops: (state) => state.shops,
    shopsMeta: (state) => state.shopsMeta,
    currentShop: (state) => state.currentShop,
    societies: (state) => state.societies,
    societiesMeta: (state) => state.societiesMeta,
    currentSociety: (state) => state.currentSociety,
    loadingDashboard: (state) => state.loadingDashboard,
    loadingAggregate: (state) => state.loadingAggregate,
    modules: (state) => state.modules,
    plans: (state) => state.plans,
    countries: (state) => state.countries,
    sectors: (state) => state.sectors,
    tickets: (state) => state.tickets,
    ticketsMeta: (state) => state.ticketsMeta,
    supportSettings: (state) => state.supportSettings,
    supportContactSettings: (state) => state.supportContactSettings,
}

const mutations = {
    SET_DASHBOARD(state, data) {
        state.dashboard = data
    },
    SET_AGGREGATE(state, data) {
        state.aggregate = data
    },
    SET_SHOPS(state, { data, meta }) {
        state.shops = data
        state.shopsMeta = meta
    },
    SET_CURRENT_SHOP(state, data) {
        state.currentShop = data
    },
    SET_SOCIETIES(state, { data, meta }) {
        state.societies = data
        state.societiesMeta = meta
    },
    SET_CURRENT_SOCIETY(state, data) {
        state.currentSociety = data
    },
    SET_LOADING_DASHBOARD(state, val) {
        state.loadingDashboard = val
    },
    SET_LOADING_AGGREGATE(state, val) {
        state.loadingAggregate = val
    },
    SET_MODULES(state, data) {
        state.modules = data
    },
    SET_PLANS(state, data) {
        state.plans = data
    },
    SET_COUNTRIES(state, data) {
        state.countries = data
    },
    SET_SECTORS(state, data) {
        state.sectors = data
    },
    SET_TICKETS(state, { data, meta }) {
        state.tickets = data
        state.ticketsMeta = meta
    },
    SET_SUPPORT_SETTINGS(state, data) {
        state.supportSettings = data
    },
    SET_SUPPORT_CONTACT_SETTINGS(state, data) {
        state.supportContactSettings = data
    },
}

const actions = {
    fetchDashboard({ commit }) {
        commit('SET_LOADING_DASHBOARD', true)
        return landlordAxios.get('landlord/dashboard').then((response) => {
            commit('SET_DASHBOARD', response.data.data)
            return response
        }).finally(() => {
            commit('SET_LOADING_DASHBOARD', false)
        })
    },

    fetchAggregate({ commit }) {
        commit('SET_LOADING_AGGREGATE', true)
        return landlordAxios.get('landlord/dashboard/aggregate').then((response) => {
            commit('SET_AGGREGATE', response.data.data)
            return response
        }).finally(() => {
            commit('SET_LOADING_AGGREGATE', false)
        })
    },

    fetchShopStats(_, shopId) {
        return landlordAxios.get(`landlord/dashboard/shops/${shopId}/stats`)
    },

    fetchCountries({ commit }) {
        return landlordAxios.get('get/countries').then((response) => {
            commit('SET_COUNTRIES', response.data.countries)
            return response
        })
    },

    fetchSectors({ commit }) {
        return landlordAxios.get('sectors/public').then((response) => {
            commit('SET_SECTORS', response.data.data)
            return response
        })
    },

    createShop(_, formData) {
        return landlordAxios.post('landlord/shops', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },

    fetchShops({ commit }, params = {}) {
        return landlordAxios.get('landlord/shops', { params }).then((response) => {
            commit('SET_SHOPS', {
                data: response.data.data,
                meta: response.data.meta
            })
            return response
        })
    },

    fetchShop({ commit }, shopId) {
        return landlordAxios.get(`landlord/shops/${shopId}`).then((response) => {
            commit('SET_CURRENT_SHOP', response.data.data)
            return response
        })
    },

    fetchSocieties({ commit }, params = {}) {
        return landlordAxios.get('landlord/societies', { params }).then((response) => {
            commit('SET_SOCIETIES', {
                data: response.data.data,
                meta: response.data.meta
            })
            return response
        })
    },

    fetchSociety({ commit }, societyId) {
        return landlordAxios.get(`landlord/societies/${societyId}`).then((response) => {
            commit('SET_CURRENT_SOCIETY', response.data.data)
            return response
        })
    },

    updateSociety({ commit }, { societyId, data }) {
        return landlordAxios.put(`landlord/societies/${societyId}`, data).then((response) => {
            commit('SET_CURRENT_SOCIETY', response.data.data)
            return response
        })
    },

    updateShop(_, { shopId, data }) {
        if (data instanceof FormData) {
            data.append('_method', 'PUT')
            return landlordAxios.post(`landlord/shops/${shopId}`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
        }
        return landlordAxios.put(`landlord/shops/${shopId}`, data)
    },

    // inscription depuis la landing : démarre l'abonnement et envoie les accès au client
    validateSignup(_, { shopId, data = {} }) {
        return landlordAxios.post(`landlord/shops/${shopId}/validate-signup`, data)
    },

    toggleShopStatus(_, shopId) {
        return landlordAxios.patch(`landlord/shops/${shopId}/toggle-status`)
    },

    setLimits(_, { shopId, limits }) {
        return landlordAxios.patch(`landlord/shops/${shopId}/set-limits`, limits)
    },

    deleteShop(_, shopId) {
        return landlordAxios.delete(`landlord/shops/${shopId}`)
    },

    fetchDomain(_, shopId) {
        return landlordAxios.get(`landlord/shops/${shopId}/domain`)
    },

    updateDomain(_, { shopId, domain }) {
        return landlordAxios.put(`landlord/shops/${shopId}/domain`, { domain })
    },

    // ── Modules ──
    fetchModules({ commit }) {
        return landlordAxios.get('landlord/modules').then((response) => {
            commit('SET_MODULES', response.data.data)
            return response
        })
    },

    createModule(_, data) {
        return landlordAxios.post('landlord/modules', data)
    },

    updateModule(_, { moduleId, data }) {
        return landlordAxios.put(`landlord/modules/${moduleId}`, data)
    },

    deleteModule(_, moduleId) {
        return landlordAxios.delete(`landlord/modules/${moduleId}`)
    },

    // ── Plans ──
    fetchPlans({ commit }) {
        return landlordAxios.get('landlord/plans').then((response) => {
            commit('SET_PLANS', response.data.data)
            return response
        })
    },

    createPlan(_, data) {
        return landlordAxios.post('landlord/plans', data)
    },

    fetchPlan(_, planId) {
        return landlordAxios.get(`landlord/plans/${planId}`)
    },

    updatePlan(_, { planId, data }) {
        return landlordAxios.put(`landlord/plans/${planId}`, data)
    },

    deletePlan(_, planId) {
        return landlordAxios.delete(`landlord/plans/${planId}`)
    },

    syncPlanModules(_, { planId, moduleIds }) {
        return landlordAxios.post(`landlord/plans/${planId}/sync-modules`, { module_ids: moduleIds })
    },

    // ── Shop Users ──
    fetchShopUsers(_, shopId) {
        return landlordAxios.get(`landlord/shops/${shopId}/users`)
    },

    createShopUser(_, { shopId, data }) {
        return landlordAxios.post(`landlord/shops/${shopId}/users`, data)
    },

    updateShopUser(_, { shopId, userId, data }) {
        return landlordAxios.put(`landlord/shops/${shopId}/users/${userId}`, data)
    },

    toggleShopUserActive(_, { shopId, userId }) {
        return landlordAxios.patch(`landlord/shops/${shopId}/users/${userId}/toggle-active`)
    },

    // ── Shop License ──
    fetchShopLicense(_, shopId) {
        return landlordAxios.get(`landlord/shops/${shopId}/license`)
    },

    assignPlan(_, { shopId, data }) {
        return landlordAxios.post(`landlord/shops/${shopId}/license/assign-plan`, data)
    },

    toggleModule(_, { shopId, data }) {
        return landlordAxios.post(`landlord/shops/${shopId}/license/toggle-module`, data)
    },

    revokePlan(_, shopId) {
        return landlordAxios.patch(`landlord/shops/${shopId}/license/revoke-plan`)
    },

    removeOverride(_, { shopId, moduleId }) {
        return landlordAxios.delete(`landlord/shops/${shopId}/license/override/${moduleId}`)
    },

    fetchActiveModules(_, shopId) {
        return landlordAxios.get(`landlord/shops/${shopId}/license/active-modules`)
    },

    // ── Branding ──
    fetchShopBranding(_, shopId) {
        return landlordAxios.get(`landlord/shops/${shopId}/branding`)
    },

    updateShopBranding(_, { shopId, data }) {
        return landlordAxios.put(`landlord/shops/${shopId}/branding`, data)
    },

    // ── Tickets support ──
    fetchTickets({ commit }, params = {}) {
        return landlordAxios.get('landlord/tickets', { params }).then((response) => {
            commit('SET_TICKETS', {
                data: response.data.data,
                meta: response.data.meta
            })
            return response
        })
    },

    fetchTicket(_, ticketId) {
        return landlordAxios.get(`landlord/tickets/${ticketId}`)
    },

    updateTicketStatus(_, { ticketId, status }) {
        return landlordAxios.patch(`landlord/tickets/${ticketId}/status`, { status })
    },

    replyToTicket(_, { ticketId, reply }) {
        return landlordAxios.patch(`landlord/tickets/${ticketId}/reply`, { reply })
    },

    fetchSupportSettings({ commit }) {
        return landlordAxios.get('landlord/tickets/settings').then((response) => {
            commit('SET_SUPPORT_SETTINGS', response.data.data)
            return response
        })
    },

    updateSupportSetting(_, { priority, data }) {
        return landlordAxios.patch(`landlord/tickets/settings/${priority}`, data)
    },

    fetchSupportContactSettings({ commit }) {
        return landlordAxios.get('landlord/tickets/contact-settings').then((response) => {
            commit('SET_SUPPORT_CONTACT_SETTINGS', response.data.data)
            return response
        })
    },

    updateSupportContactSettings({ commit }, data) {
        return landlordAxios.put('landlord/tickets/contact-settings', data).then((response) => {
            commit('SET_SUPPORT_CONTACT_SETTINGS', response.data.data)
            return response
        })
    },
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
}
